import React from 'react';
import Brochure, {printDocument} from 'components/Brochure';
import {useTranslation} from 'react-i18next';
import {useCopyToClipboard} from 'react-use';
import {useAuth} from 'hooks/useAuth';
import {get} from 'lodash';
import useCustomToast from 'hooks/useCustomToast';
import CustomDialog from './CustomDialog';
import {Stack, Text} from '@chakra-ui/react';
import {usePlatformBasicInfo} from 'hooks/datahook/auth';

/**
 * Get IB referral link dialog.
 *
 * The campaign is picked OUTSIDE the dialog: the parent owns the campaign
 * list and hands the picked one in as the `campaign` prop (see
 * page/app/IB/DashboardTiles.js, where the selector sits next to the
 * "Get IB Referral link" button). The dialog therefore never fetches
 * campaigns and never renders a picker — it only shows the link, the QR code
 * and the invitation image of the campaign it was given.
 *
 * Without a campaign (a plain client — the backend only materialises a
 * default campaign for IBs) it falls back to the profile referral code link,
 * i.e. the behaviour this dialog had before the campaign scope was added.
 */
const BrochureDialog = (props) => {
    const {open, onClose, profile, campaign} = props;
    const {t} = useTranslation('app');
    const {isUser} = useAuth();
    const [state, copyToClipboard] = useCopyToClipboard();
    const toast = useCustomToast();
    const {data: platformBasicInfo} = usePlatformBasicInfo();

    const origin = isUser
        ? window.location.origin
        : 'https://portal.oxsecurities.com';

    // Only an approved campaign carries a linkCode; a pending one has none and
    // must fall back to the profile referral code instead of an empty code.
    const referralCode =
        campaign?.linkCode || get(profile, 'referralReference') || '';
    const link = referralCode ? `${origin}/auth/sign-up?code=${referralCode}` : '';

    const onCopyLink = () => {
        copyToClipboard(link);
        toast.show({title: t('copied')});
    };

    return (
        <CustomDialog
            open={open}
            onClose={onClose}
            title={platformBasicInfo?.companyShortName}
            contentClass="nx-dialog"
            actions={
                link ? (
                    <>
                        {/* todo: crm 146 remove download */}
                        {/* <button
                            type="button"
                            className="btn"
                            onClick={() => printDocument({id: 'brochure'})}
                        >
                            {t('download')}
                        </button> */}
                        <button
                            type="button"
                            className="btn"
                            onClick={onCopyLink}
                        >
                            {t('copyLink')}
                        </button>
                    </>
                ) : undefined
            }
        >
            <Stack spacing={3} mb={4}>
                {link && (
                    <Text className="mono" fontSize="sm">
                        {link}
                    </Text>
                )}
            </Stack>
            {link && (
                <Brochure
                    profile={profile}
                    platformBasicInfo={platformBasicInfo}
                    referralLink={link}
                    referralCode={referralCode}
                />
            )}
        </CustomDialog>
    );
};

export default BrochureDialog;
